import { assert } from '../../errors';
import {
    classicPortfolioInfoByRiskScore,
    directIndexingInfoByRiskScore,
} from '../../goals';
import { GoalAccountStructure } from '../../graphql/generated';
import styleguide from '../../styleguide';
import { GoalAccountInvestmentType } from '../../types';
import { toPercentage } from '../../util/numbers';
import { typedKeys } from '../../util/objects';
import { isNotNullish, isNumber } from '../../util/types';
import { infoByGoalAccountStructure } from './goalAccountStructure';

const SECTOR_MAP: Record<Sector, { color: string; label: string }> = {
    stocksUS: {
        color: '#185B83',
        label: 'US stocks',
    },
    stocksIntl: {
        color: '#569DC6',
        label: 'International stocks',
    },
    bondsUS: {
        color: '#89C0E0',
        label: 'US bonds',
    },
    treasuryUS: {
        color: '#C4E7FB',
        label: 'US treasuries',
    },
    bondsAndTreasuryUS: {
        color: '#89C0E0',
        label: 'US treasuries and bonds',
    },
    mmf: {
        color: styleguide.colors.greenLightX,
        label: 'Money market funds',
    },
} as const;

export const getSectorProps = ({
    investmentType,
    riskScore,
}: {
    investmentType: GoalAccountInvestmentType;
    riskScore: number;
}): SectorProp[] => {
    if (investmentType === 'HIGH_YIELD_CASH') {
        return [
            {
                key: 'mmf',
                value: 100,
                label: SECTOR_MAP.mmf.label,
                color: SECTOR_MAP.mmf.color,
            },
        ];
    }

    // DIRECT_INDEXING / CLASSIC_PORTFOLIO
    assert(riskScore >= 1 && riskScore <= 10, 'Invalid risk score');

    const infoByRiskScore = {
        DIRECT_INDEXING: directIndexingInfoByRiskScore,
        CLASSIC_PORTFOLIO: classicPortfolioInfoByRiskScore,
    }[investmentType];

    const { composition } = infoByRiskScore[riskScore];

    return typedKeys(composition)
        .map((key) => {
            const value = composition[key];
            if (!isNumber(value)) return null;

            const { color, label } = SECTOR_MAP[key];

            return {
                key,
                color,
                label,
                value: toPercentage(value),
            };
        })
        .filter(isNotNullish);
};

export const getInvestmentType = ({
    accountStructure,
    riskScore,
}: {
    accountStructure: GoalAccountStructure;
    riskScore: number;
}): GoalAccountInvestmentType => {
    return riskScore === 0
        ? 'HIGH_YIELD_CASH'
        : infoByGoalAccountStructure[accountStructure].investmentTypes.includes(
                'DIRECT_INDEXING'
            )
          ? 'DIRECT_INDEXING'
          : 'CLASSIC_PORTFOLIO';
};

export type Sector =
    | 'mmf'
    | 'stocksUS'
    | 'stocksIntl'
    | 'treasuryUS'
    | 'bondsUS'
    | 'bondsAndTreasuryUS';

export type SectorProp = {
    key: Sector;
    value: number;
    color: string;
    label: string;
};
